/**
 * Optimized performance monitoring utilities
 * Provides efficient performance tracking with minimal overhead
 */

import type { LogContext } from '../logger/index.js';
import type { PerformanceMetric, PerformanceMetricId } from '../types/common.js';
import { generatePerformanceMetricId } from '../types/branded.js';

// Performance monitoring configuration
export interface PerformanceConfig {
  readonly enabled: boolean;
  readonly maxMetrics: number;
  readonly enableMemoryTracking: boolean;
  readonly enableCpuTracking: boolean;
  readonly sampleRate: number; // 0.0 to 1.0
}

// Optimized performance monitor with memory management
export class OptimizedPerformanceMonitor {
  private static readonly activeMetrics = new Map<PerformanceMetricId, PerformanceMetric>();
  private static readonly completedMetrics: PerformanceMetric[] = [];
  private static config: PerformanceConfig = {
    enabled: process.env.NODE_ENV === 'development' || process.env.PERFORMANCE_MONITORING === 'true',
    maxMetrics: 1000,
    enableMemoryTracking: true,
    enableCpuTracking: false, // CPU tracking is expensive
    sampleRate: 1.0
  };

  /**
   * Configure performance monitoring
   */
  static configure(config: Partial<PerformanceConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Start performance monitoring with sampling
   */
  static startMetric(name: string, context?: LogContext): PerformanceMetricId | null {
    if (!this.config.enabled || Math.random() > this.config.sampleRate) {
      return null;
    }

    const id = generatePerformanceMetricId();
    const metric: PerformanceMetric = {
      id,
      name,
      startTime: performance.now(),
      context
    };

    this.activeMetrics.set(id, metric);
    return id;
  }

  /**
   * End performance monitoring and return the metric
   */
  static endMetric(id: PerformanceMetricId | null): PerformanceMetric | null {
    if (!id || !this.config.enabled) {
      return null;
    }

    const metric = this.activeMetrics.get(id);
    if (!metric) {
      return null;
    }

    const endTime = performance.now();
    const completedMetric: PerformanceMetric = {
      ...metric,
      endTime,
      duration: endTime - metric.startTime,
      memoryUsage: this.config.enableMemoryTracking ? process.memoryUsage() : undefined
    };

    this.activeMetrics.delete(id);
    this.addCompletedMetric(completedMetric);

    return completedMetric;
  }

  /**
   * Add completed metric with memory management
   */
  private static addCompletedMetric(metric: PerformanceMetric): void {
    this.completedMetrics.push(metric);
    
    // Clean up old metrics to prevent memory leaks
    if (this.completedMetrics.length > this.config.maxMetrics) {
      this.completedMetrics.splice(0, this.completedMetrics.length - this.config.maxMetrics);
    }
  }

  /**
   * Get performance summary with efficient calculations
   */
  static getPerformanceSummary(): {
    totalMetrics: number;
    averageDuration: number;
    slowestOperations: PerformanceMetric[];
    memoryPeak: NodeJS.MemoryUsage | null;
    recentMetrics: PerformanceMetric[];
  } {
    const totalMetrics = this.completedMetrics.length;
    
    if (totalMetrics === 0) {
      return {
        totalMetrics: 0,
        averageDuration: 0,
        slowestOperations: [],
        memoryPeak: null,
        recentMetrics: []
      };
    }

    // Calculate average duration efficiently
    let totalDuration = 0;
    let memoryPeak: NodeJS.MemoryUsage | null = null;
    
    for (const metric of this.completedMetrics) {
      if (metric.duration) {
        totalDuration += metric.duration;
      }
      
      if (metric.memoryUsage && this.config.enableMemoryTracking) {
        if (!memoryPeak) {
          memoryPeak = { ...metric.memoryUsage };
        } else {
          memoryPeak.rss = Math.max(memoryPeak.rss, metric.memoryUsage.rss);
          memoryPeak.heapTotal = Math.max(memoryPeak.heapTotal, metric.memoryUsage.heapTotal);
          memoryPeak.heapUsed = Math.max(memoryPeak.heapUsed, metric.memoryUsage.heapUsed);
          memoryPeak.external = Math.max(memoryPeak.external, metric.memoryUsage.external);
          memoryPeak.arrayBuffers = Math.max(memoryPeak.arrayBuffers, metric.memoryUsage.arrayBuffers);
        }
      }
    }

    const averageDuration = totalDuration / totalMetrics;

    // Get slowest operations (top 10)
    const slowestOperations = [...this.completedMetrics]
      .filter(m => m.duration !== undefined)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .slice(0, 10);

    // Get recent metrics (last 50)
    const recentMetrics = this.completedMetrics.slice(-50);

    return {
      totalMetrics,
      averageDuration,
      slowestOperations,
      memoryPeak,
      recentMetrics
    };
  }

  /**
   * Clear all metrics (useful for testing or session cleanup)
   */
  static clearMetrics(): void {
    this.activeMetrics.clear();
    this.completedMetrics.length = 0;
  }

  /**
   * Get current configuration
   */
  static getConfig(): PerformanceConfig {
    return { ...this.config };
  }
}

// High-performance timing utilities
export class HighPerformanceTimer {
  private static readonly timers = new Map<string, number>();

  /**
   * Start a timer with minimal overhead
   */
  static start(label: string): void {
    if (!OptimizedPerformanceMonitor.getConfig().enabled) {
      return;
    }
    this.timers.set(label, performance.now());
  }

  /**
   * End a timer and return duration
   */
  static end(label: string): number | null {
    if (!OptimizedPerformanceMonitor.getConfig().enabled) {
      return null;
    }

    const startTime = this.timers.get(label);
    if (startTime === undefined) {
      return null;
    }

    const duration = performance.now() - startTime;
    this.timers.delete(label);
    return duration;
  }

  /**
   * Time an operation with minimal overhead
   */
  static async time<T>(label: string, operation: () => Promise<T>): Promise<T> {
    this.start(label);
    try {
      return await operation();
    } finally {
      const duration = this.end(label);
      if (duration !== null && duration > 100) { // Only log slow operations
        console.log(`⏱️  ${label}: ${duration.toFixed(2)}ms`);
      }
    }
  }

  /**
   * Time a synchronous operation
   */
  static timeSync<T>(label: string, operation: () => T): T {
    this.start(label);
    try {
      return operation();
    } finally {
      const duration = this.end(label);
      if (duration !== null && duration > 10) { // Only log slow operations
        console.log(`⏱️  ${label}: ${duration.toFixed(2)}ms`);
      }
    }
  }
}

// Memory monitoring utilities
export class MemoryMonitor {
  private static readonly samples: NodeJS.MemoryUsage[] = [];
  private static readonly maxSamples = 100;

  /**
   * Sample current memory usage
   */
  static sample(): NodeJS.MemoryUsage {
    const usage = process.memoryUsage();
    this.samples.push(usage);
    
    // Keep only recent samples
    if (this.samples.length > this.maxSamples) {
      this.samples.shift();
    }
    
    return usage;
  }

  /**
   * Get memory statistics
   */
  static getStats(): {
    current: NodeJS.MemoryUsage;
    average: NodeJS.MemoryUsage | null;
    peak: NodeJS.MemoryUsage | null;
    trend: 'increasing' | 'decreasing' | 'stable';
  } {
    const current = process.memoryUsage();
    
    if (this.samples.length === 0) {
      return {
        current,
        average: null,
        peak: null,
        trend: 'stable'
      };
    }

    // Calculate average
    const average: NodeJS.MemoryUsage = {
      rss: 0,
      heapTotal: 0,
      heapUsed: 0,
      external: 0,
      arrayBuffers: 0
    };

    let peak: NodeJS.MemoryUsage | null = null;

    for (const sample of this.samples) {
      average.rss += sample.rss;
      average.heapTotal += sample.heapTotal;
      average.heapUsed += sample.heapUsed;
      average.external += sample.external;
      average.arrayBuffers += sample.arrayBuffers;

      if (!peak || sample.heapUsed > peak.heapUsed) {
        peak = sample;
      }
    }

    const count = this.samples.length;
    average.rss /= count;
    average.heapTotal /= count;
    average.heapUsed /= count;
    average.external /= count;
    average.arrayBuffers /= count;

    // Determine trend
    let trend: 'increasing' | 'decreasing' | 'stable' = 'stable';
    if (this.samples.length >= 3) {
      const recent = this.samples.slice(-3);
      const first = recent[0].heapUsed;
      const last = recent[2].heapUsed;
      const change = (last - first) / first;
      
      if (change > 0.1) trend = 'increasing';
      else if (change < -0.1) trend = 'decreasing';
    }

    return {
      current,
      average,
      peak,
      trend
    };
  }

  /**
   * Check for memory leaks
   */
  static checkForLeaks(): {
    hasLeak: boolean;
    confidence: number;
    details: string;
  } {
    const stats = this.getStats();
    
    if (!stats.average || !stats.peak) {
      return {
        hasLeak: false,
        confidence: 0,
        details: 'Insufficient data for leak detection'
      };
    }

    const currentHeap = stats.current.heapUsed;
    const averageHeap = stats.average.heapUsed;
    const peakHeap = stats.peak.heapUsed;

    // Check if current usage is significantly above average
    const aboveAverage = currentHeap > averageHeap * 1.5;
    const nearPeak = currentHeap > peakHeap * 0.9;
    const increasingTrend = stats.trend === 'increasing';

    let hasLeak = false;
    let confidence = 0;
    let details = '';

    if (aboveAverage && nearPeak && increasingTrend) {
      hasLeak = true;
      confidence = 0.8;
      details = 'Strong indicators of memory leak detected';
    } else if (aboveAverage && increasingTrend) {
      hasLeak = true;
      confidence = 0.6;
      details = 'Possible memory leak detected';
    } else if (aboveAverage) {
      hasLeak = false;
      confidence = 0.3;
      details = 'Memory usage above average but no clear leak pattern';
    } else {
      hasLeak = false;
      confidence = 0.1;
      details = 'Memory usage appears normal';
    }

    return { hasLeak, confidence, details };
  }

  /**
   * Clear memory samples
   */
  static clear(): void {
    this.samples.length = 0;
  }
}

// Export optimized performance utilities
export const performance = {
  monitor: OptimizedPerformanceMonitor,
  timer: HighPerformanceTimer,
  memory: MemoryMonitor
};
